import Bugsnag from "@bugsnag/js";
import { AxiosInstance, AxiosPromise, AxiosRequestConfig, AxiosResponse } from "axios";

import { client } from "./typesense-client";

import { NotImplementedException } from "./exceptions/not-implemented.exception";
import { NotFoundProviderException } from "./exceptions/not-found-provider.exception";
import { ErrorsProviderException } from "./exceptions/errors-provider.exception";
import { GenericProviderException } from "./exceptions/generic-provider.exception";
import { filterSensitiveDataFromJSON } from "@/services/utils/string.utils";

/**
 * Default data type for URL options.
 */
export interface IOptionsType {
  [key: string]: any;
}

/**
 * List of features that a Provider can have.
 */
export enum EProviderFeatures {
  LIST,
  GET
}


export abstract class TypesenseProvider<T> {
  // Axios Instance
  protected httpClient: AxiosInstance = client;

  /**
   * Endpoint to be used to make the API call.
   */
  private readonly endpoint!: string;

  /**
   * List of features that this provider supports.
   */
  private features: Array<EProviderFeatures> = [];

  /**
   * Create a new Provider instance.
   *
   * @param endpoint Endpoint to be used to make the API call.
   * @param features
   * @param features
   */
  constructor(endpoint = "", features: Array<EProviderFeatures> = []) {
    this.endpoint = endpoint;

    if (this.features.length === 0) {
      this.features = [
        EProviderFeatures.LIST,
        EProviderFeatures.GET,
      ];
    } else {
      this.features = features;
    }
  }

  /**
   * Build the endpoint url with the query string parameters.
   */
  protected buildEndPointUrl(
    options: IOptionsType = {},
    preQuery?: string,
  ): string {
    const base = preQuery ? `${this.endpoint}/${preQuery}` : this.endpoint;
    let queryString = "";

    for (const key in options) {
      if (!Object.prototype.hasOwnProperty.call(options, key)) {
        continue;
      }

      const value = options[key];
      if (queryString.length !== 0) {
        queryString += "&";
      }
      queryString += `${key}=${value}`;
    }

    if (queryString.length > 0) {
      return `${base}?${queryString}`;
    }

    return base;
  }

  /**
   * Fetch a list of resources.
   *
   * @param options Additional options to be passed as query parameters.
   */
  public async list(
    options: IOptionsType = {},
    axiosOptions: AxiosRequestConfig = {},
  ): Promise<Array<T>> {
    if (!this.features.includes(EProviderFeatures.LIST)) {
      throw new NotImplementedException();
    }

    const url = this.buildEndPointUrl(options);
    const request = this.httpClient.get(url, axiosOptions);
    const { data } = await this.wrapRequest(request);

    return data as Array<T>;
  }


  /**
   * Get a specific resources by it's identifier.
   *
   * @param id Identifier for the resource to be retrieved.
   * @param options Additional options to be passed as query parameters.
   */
  public async get<IDT = number>(
    id: IDT,
    options: IOptionsType = {},
  ): Promise<T> {
    if (!this.features.includes(EProviderFeatures.GET)) {
      throw new NotImplementedException();
    }

    const url = this.buildEndPointUrl(options, String(id));
    const request = this.httpClient.get(url);
    const { data } = await this.wrapRequest(request);

    return data as T;
  }



  /**
   * Wrap the HTTP request to format the errors and abstract
   * it form each request.
   *
   * @param request Axios request to be made.
   */
  protected async wrapRequest(request: AxiosPromise): Promise<any> {
    try {
      return await request;
    } catch (error: AxiosResponse | any) {
      if (error?.cancelled) {
        return;
      }
      this.notifyErrorOnBugsnag(error);
      throw this.handleAxiosError(error);
    }
  }

  /**
   * Handle the axios error response.
   *
   * This objective of the method is to convert an errors
   * response into an exception with a specific Error type.
   *
   * @param axiosError Error response.
   */
  protected handleAxiosError(axiosError: AxiosResponse): Error {
    if (!!axiosError && axiosError.status === 404) {
      return new NotFoundProviderException(axiosError);
    } else if (
      !!axiosError &&
      axiosError.status === 400 &&
      axiosError.data.errors
    ) {
      return new ErrorsProviderException(axiosError);
    }

    return new GenericProviderException(axiosError);
  }


  /**
   * Notify all request errors to Bugsnag.
   *
   * In efforts to proactively act upon handled or unhandled
   * errors that break the app we're notifying all request
   * errors with its necessary context:
   * -> Vuex store
   * -> Identified user
   * -> Request and API Response
   *
   * @param axiosError Error response.
   */
  protected notifyErrorOnBugsnag(axiosError?: AxiosResponse): void {
    // TODO: Find a proper way to import the store module while avoiding a circular import:
    // https://stackoverflow.com/questions/38841469/how-to-fix-this-es6-module-circular-dependency
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const store = require("@/services/store").default;

    const statusCode = !!axiosError ? axiosError.status.toString() : "503";
    const isServerError = statusCode.startsWith("5");
    const errorType = isServerError ? "API internal" : "Client";

    Bugsnag.notify(
      new Error(
        !!axiosError
          ? `Yikes! ${errorType} error (${statusCode}) occurred while requesting: ${axiosError.config.url}.`
          : `Yikes! ${errorType} error (${statusCode}) when attempting to make a request.`,
      ),
      (event) => {
        // Include store
        event.addMetadata("Store", {
          store: filterSensitiveDataFromJSON(window.localStorage?.vuex || ""),
        });

        // Include auth or pending user
        const authState = store.get("auth") || null;
        const pendingEntrepreneur = store.get("viralLevel/pendingUser") || null;
        const pendingSupporter = store.get("supporterFlow/value") || null;
        const userId = authState?.profileId || "";
        const userName = authState?.company?.data?.name || "";
        const userEmail =
          authState?.user?.email ||
          pendingEntrepreneur?.email ||
          pendingSupporter?.email ||
          "";
        event.setUser(userId, userEmail, userName);

        if (!!axiosError) {
          // Include API request data
          event.addMetadata("API Request", {
            url: axiosError.config.url,
            response: axiosError.data,
            method: axiosError.config.method,
            body: filterSensitiveDataFromJSON(axiosError.config.data),
            withCredentials: axiosError.config.withCredentials,
          });
        }

        // Group error by URL context and its status code
        event.groupingHash = `${event.context || ""} status:${statusCode}`;
      },
    );
  }

}